import { ContentfulFAQ, getContentfulFAQ } from 'api/contentful/getContentfulFAQ'
import {
  ContentfulMediaMentions,
  getContentfulMediaMentions,
} from 'api/contentful/getContentfulMediaMention'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import { guidebookSteps } from 'components/content/guidebook/content'
import { LovedOnesSection } from 'components/content/lovedOnesSection/LovedOnesSection'
import { ThemedTextImageSection } from 'components/content/themedTextImageSection/ThemedTextImageSection'
import { CornerstoneHero } from 'components/cornerstone/cornerstoneHero/CornerstoneHero'
import {
  getCornerstoneGradient,
  mapUrlToInsideIfLoggedIn,
} from 'components/cornerstone/CornerstoneUtils'
import { FaqTwoColumns } from 'components/faqTwoColumn/FaqTwoColumn'
import { Footer } from 'components/footer/Footer'
import { TopNavigationLandingPage } from 'components/header/topNavigationLandingPage/TopNavigationLandingPage'
import { Icon, IconName } from 'components/icon'
import { ContentfulImage } from 'components/Image'
import { LandingPageTextImageMediaMentions } from 'components/landingPageTextImageSection/LandingPageTextImageMediaMentions'
import { Layout } from 'components/layout/Layout'
import { PrimaryLink } from 'components/Link'
import { PageMeta } from 'components/PageMeta'
import { PageTitle } from 'components/PageTitle'
import { Picture } from 'components/picture/Picture'
import { SquooshedPictureKey } from 'components/picture/pictures'
import { SecuritySection } from 'components/security/SecuritySection'
import { Signup, ToneContext } from 'components/signup/Signup'
import { Spacer } from 'components/spacer/Spacer'
import { TitledStepsGuide } from 'components/titledStepsGuide/TitledStepsGuide'
import { Text } from 'components/Typography'
import { GetStaticProps } from 'next'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { useAuth } from 'utils/auth/hooks/useAuth'
import { REVALIDATE_SECS } from 'utils/constants'
import { useContentfulImages } from 'utils/contentful/ContentfulImagesContext'
import { getContentfulImages } from 'utils/contentful/getPageImages'
import { numberRange } from 'utils/general'
import { useWindowSize } from 'utils/hooks'
import { getIntlProps } from 'utils/i18n/getIntlProps'
import { mapKeysToTrans } from 'utils/i18n/mapKeysToTrans'
import { NumberRange } from 'utils/types'
import { MediaMentionsRow } from '../components/content/mediaMentions/MediaMentionsRow'

interface DigitalExecutorPageStaticProps {
  mediaMentions: ContentfulMediaMentions
  faq: ContentfulFAQ
}

const stepVisuals: Record<NumberRange<3>, { icon: IconName; picture: SquooshedPictureKey }> = {
  '1': { icon: 'TapGesture', picture: 'illustrations/digitalExecutorOrders' },
  '2': { icon: 'FileUploadCloud', picture: 'illustrations/ready' },
  '3': { icon: 'DocumentReady', picture: 'illustrations/happyDance' },
}

export default function Page(props: DigitalExecutorPageStaticProps) {
  const { t } = useTranslation('digital_executor')
  const { t: tEolGuide } = useTranslation('eol_guide')
  const images = useContentfulImages()
  const { isDesktop } = useWindowSize({ isDesktop: true })
  const { isLogged } = useAuth()

  const wgsFlowUrl = mapUrlToInsideIfLoggedIn('/wgs', isLogged)

  const signupPerks = mapKeysToTrans(
    t,
    numberRange(4).map((i) => `digital_executor.section_signup.perk_${i}` as const)
  )

  const mediaMentions = { items: props.mediaMentions?.items.slice(0, 5) ?? [] }

  return (
    <Layout
      header={<TopNavigationLandingPage variant="full" />}
      footer={<Footer variant="full" />}
      css={{ background: getCornerstoneGradient('digital-executor') }}
    >
      <PageTitle title={t('digital_executor.meta_title')} />
      <PageMeta
        description={t('digital_executor.meta_description')}
        image="/images/preview/digital-executor.png"
      />

      <Spacer size={[0, 6]} />

      <Block>
        <CornerstoneHero
          cornerstoneType="digital-executor"
          title={t('digital_executor.hero.title')}
          image={
            isDesktop ? (
              <ContentfulImage {...images.digital_executor_section_hero} />
            ) : (
              <ContentfulImage {...images.digital_executor_section_hero_mobile} />
            )
          }
        >
          <Text>{t('digital_executor.hero.description')}</Text>
          <Spacer size={[1, 1.5]} />
          <Button type="button" size={['medium', 'large']} href={wgsFlowUrl} renderAsButton>
            {t('digital_executor.hero.cta')}
          </Button>
          <Spacer size={[0, 1.5]} />
          <LandingPageTextImageMediaMentions mentions={mediaMentions} className="desktop-only" />
        </CornerstoneHero>
      </Block>

      <Spacer size={[2, 8]} />

      <Block className="mobile-only">
        <MediaMentionsRow
          mediaMentions={mediaMentions}
          hideQuotes
          title={t('digital_executor.hero.mentions_caption')}
        />
      </Block>
      <Spacer size={2} className="mobile-only" />

      <LovedOnesSection />

      <Spacer size={[2, 8]} />

      <Block>
        <TitledStepsGuide
          title={t('digital_executor.steps_guide.title')}
          steps={numberRange(3).map((i) => ({
            title: t(`digital_executor.steps_guide.step_${i}.title`),
            desc: t(`digital_executor.steps_guide.step_${i}.desc`),
            visual: <Picture imageKey={stepVisuals[i].picture} />,
            iconName: stepVisuals[i].icon,
          }))}
          singleLine
          buttonProps={{
            href: wgsFlowUrl,
            children: t('digital_executor.steps_guide.cta'),
          }}
        />
      </Block>

      <Spacer size={[2, 6]} />

      <ThemedTextImageSection
        imagePlacement="right"
        title={t('digital_executor.section_select_sites.title')}
        image={<ContentfulImage {...images.digital_executor_section_select_sites} />}
        overflowingImage
      >
        <Text>{t('digital_executor.section_select_sites.description')}</Text>
        <Button
          href="/wgs"
          type="button"
          size="large"
          variant="text"
          leftIcon={<Icon name="Plus" />}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('digital_executor.section_select_sites.cta')}
        </Button>
      </ThemedTextImageSection>

      <Spacer size={[2, 6]} />

      <SecuritySection />

      <Spacer size={[2, 6]} />

      <ThemedTextImageSection
        imagePlacement="right"
        title={t('digital_executor.section_guide.title')}
        image={<ContentfulImage {...images.digital_executor_section_guide} />}
        overflowingImage
        disableContentGap
      >
        <Text>{t('digital_executor.section_guide.description')}</Text>
        {guidebookSteps.map((step) => (
          <PrimaryLink key={step.no} href={`/end-of-life-guide#${step.id}`}>
            {tEolGuide('eol_guide.step_x', { step: step.no })} -{' '}
            {tEolGuide(`eol_guide.step_names.${step.no}`)}
          </PrimaryLink>
        ))}

        <Spacer size={1.5} />
        <Button
          href="/end-of-life-guide"
          type="button"
          size="large"
          style={{ alignSelf: 'flex-start' }}
          leftIcon={<Icon name="Guide" />}
        >
          {t('digital_executor.section_guide.cta')}
        </Button>
      </ThemedTextImageSection>

      <Spacer size={[2, 6]} />

      <FaqTwoColumns content={props.faq} />

      <Spacer size={[2, 6]} />

      <ToneContext.Provider value="dark">
        <Signup
          caption={t('digital_executor.section_signup.caption')}
          title={t('digital_executor.section_signup.title')}
          perks={signupPerks}
        />
      </ToneContext.Provider>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps<DigitalExecutorPageStaticProps> = async (ctx) => {
  const mediaMentions = await getContentfulMediaMentions('global')
  const faq = await getContentfulFAQ('digital-executor')

  const props: DigitalExecutorPageStaticProps = {
    ...(await getContentfulImages()),
    ...(await getIntlProps(ctx, ['digital_executor', 'eol_guide'])),
    mediaMentions,
    faq,
  }

  return { props, revalidate: REVALIDATE_SECS }
}
